import { ErrorState, LoadingState } from '../../components/states';
import { formatBytes } from '../mailboxes/quota';
import { DefinitionList, Section, UnavailableNote } from '../services/components';
import { formatDateTime } from '../services/format';
import { usePortalServer } from './api';

/**
 * The Portal Server (Admin, `monitoring.view`): the server the portal itself runs on, shown apart
 * from the Cloud Server and the Mail Server. Read live by the API when the page opens; the
 * monitoring worker's heartbeat is the strip above. Portal database backups are taken by hand on
 * the server (runbook §7; destination D-3 open), so the portal cannot see them — said plainly
 * rather than shown as a status.
 */
export function PortalServerSection() {
  const query = usePortalServer();
  const refresh = (
    <button
      type="button"
      onClick={() => void query.refetch()}
      disabled={query.isFetching}
      className="rounded-lg border border-line-strong px-3 py-1.5 text-xs font-semibold text-fg hover:border-muted hover:bg-raised disabled:opacity-60"
    >
      {query.isFetching ? 'Checking…' : 'Check again'}
    </button>
  );

  return (
    <Section
      title="Portal Server"
      description="The server this portal runs on — separate from the Cloud Server and the Mail Server. Read live when this page opens."
      action={query.isPending ? undefined : refresh}
    >
      {query.isPending ? (
        <LoadingState label="Reading the Portal Server…" />
      ) : query.isError ? (
        <ErrorState
          title="The Portal Server status could not be loaded"
          message={query.error.message}
          onRetry={() => void query.refetch()}
        />
      ) : (
        <div className="space-y-4">
          <DefinitionList
            items={[
              [
                'Portal database',
                query.data.database.state === 'ok' ? (
                  <span>
                    Answering
                    {query.data.database.sizeBytes !== null
                      ? ` · ${formatBytes(query.data.database.sizeBytes)}`
                      : ''}
                  </span>
                ) : (
                  <span className="font-medium text-danger">Not answering</span>
                ),
              ],
              [
                'Disk (containers and database)',
                query.data.disk ? (
                  <span>
                    {formatBytes(query.data.disk.usedBytes)} used of{' '}
                    {formatBytes(query.data.disk.totalBytes)} ({query.data.disk.usedPercent}%) ·{' '}
                    {formatBytes(query.data.disk.availableBytes)} free
                  </span>
                ) : (
                  <span className="text-muted">Could not be read</span>
                ),
              ],
              ['Read at', formatDateTime(query.data.checkedAt)],
            ]}
          />
          <UnavailableNote>
            Portal database backups are not visible here: they are taken by hand on the server
            (production runbook §7), and where they are kept is still an open decision (D-3).
          </UnavailableNote>
        </div>
      )}
    </Section>
  );
}
